import { useNavigate } from "react-router-dom";
import { Charge } from "@/pages/sy/models/Charge";
import { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import { useUpsertChargeMutation } from "@/pages/sy/hooks/queries/useChargeMutations";

type UseCustomHandlersProps = {
  currentOperatorId: number | null;
  chargeLayers: ChargeLayer[];
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
  setIsEditingManualWeight: (isEditingManualWeight: boolean) => void;
  charge: Charge | null;
  setSelectedLayer: (layer: ChargeLayer | null) => void;
  setError: (error: string) => void;
  setNotificationMessage: (message: string) => void;
  allCharges: Charge[];
  setErrorDialogData: (
    data: {
      title: string;
      message: string;
      details?: { label: string; value: string | number | null | undefined }[];
      detailsTitle?: string;
      guidanceMessage?: string;
      isBucketConflict?: boolean;
    } | null,
  ) => void;
  setErrorDialogOpen: (open: boolean) => void;
};

export const useCustomHandlers = ({
  currentOperatorId,
  chargeLayers,
  isEditMode,
  setIsEditMode,
  setIsEditingManualWeight,
  charge,
  setSelectedLayer,
  setError,
  setNotificationMessage,
  allCharges,
  setErrorDialogData,
  setErrorDialogOpen,
}: UseCustomHandlersProps) => {
  const navigate = useNavigate();
  const upsertChargeMutation = useUpsertChargeMutation();

  const handleToggleMode = () => {
    const newEditMode = !isEditMode;
    setIsEditMode(newEditMode);
    setSelectedLayer(null);
  };

  const handleBack = () => {
    navigate("/scrap-charges/selection", {
      state: {
        previousOperatorId: currentOperatorId,
        previousChargeId: charge?.ChargeId,
        previousScheduledHeatName: charge?.ScheduledHeatName,
      },
    });
  };

  const handleStatusAction = () => {
    if (!charge?.ChargeId) return;

    let newStatus = charge.ChargeStatusCode;
    if (charge.ChargeStatusCode === "S" || charge.ChargeStatusCode === "C") {
      newStatus = "O";
    } else if (charge.ChargeStatusCode === "O") {
      newStatus = "C";
    }

    // Check for bucket conflict when opening a charge (START or REOPEN)
    if (newStatus === "O") {
      const bucketInUse = allCharges.find(
        (c: Charge) =>
          c.ChargeId !== charge.ChargeId &&
          c.BucketId === charge.BucketId &&
          (c.ChargeStatusCode === "O" || c.ChargeStatusCode === "C"),
      );

      if (bucketInUse) {
        setErrorDialogData({
          title: "Bucket Conflict Error",
          message: `Cannot open charge. Bucket ${charge.BucketName || charge.BucketId} is already in use by another open / closed charge.`,
          details: [
            { label: "Conflicting Charge ID", value: bucketInUse.ChargeId },
            {
              label: "Heat Name",
              value: bucketInUse.ScheduledHeatName || "N/A",
            },
            { label: "Charge Number", value: bucketInUse.ChargeNumber },
            { label: "Status", value: "Open" },
          ],
          detailsTitle: "Conflicting Charge Details:",
          guidanceMessage:
            "Please close the conflicting charge or select a different bucket before proceeding.",
        });
        setErrorDialogOpen(true);
        return;
      }
    }

    upsertChargeMutation.mutate(
      {
        ...charge,
        ChargeStatusCode: newStatus,
        OperatorId: currentOperatorId,
      },
      {
        onSuccess: () => {
          setNotificationMessage(
            newStatus === "O"
              ? "Charge is now Open"
              : newStatus === "C"
                ? "Charge closed successfully"
                : "Status updated",
          );

          if (newStatus === "O") {
            const currentLayer = chargeLayers.find((l) => l.IsCurrent);
            if (currentLayer) {
              setSelectedLayer(currentLayer);
            }
          }
        },
        onError: (error) => {
          setError(
            "Error updating charge status: " +
              (error instanceof Error ? error.message : String(error)),
          );
        },
      },
    );
  };

  const handleSaveManualWeight = (manualTargetWeight: number | null) => {
    if (!charge?.ChargeId) return;

    const chargeToUpdate: Charge = {
      ...charge,
      ManualTargetWeight: manualTargetWeight,
      OperatorId: currentOperatorId,
    };

    upsertChargeMutation.mutate(chargeToUpdate, {
      onSuccess: () => {
        setNotificationMessage("Manual target weight updated successfully");
        setIsEditingManualWeight(false);
      },
      onError: (error) => {
        setError(
          "Error updating manual target weight: " +
            (error instanceof Error ? error.message : String(error)),
        );
      },
    });
  };

  const handleChargeIntoEAF = () => {
    if (!charge?.ChargeId) return;

    upsertChargeMutation.mutate(
      {
        ...charge,
        ChargeStatusCode: "H",
        OperatorId: currentOperatorId,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Charge marked as Charged Into EAF");
        },
        onError: (error) => {
          setError(
            "Error charging into EAF: " +
              (error instanceof Error ? error.message : String(error)),
          );
        },
      },
    );
  };

  return {
    handleToggleMode,
    handleBack,
    handleStatusAction,
    handleSaveManualWeight,
    handleChargeIntoEAF,
    isCustomMutating: upsertChargeMutation.isPending,
  };
};
